import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Cloud, Rocket, Lightning, Code, GithubLogo, ArrowSquareOut,
  CheckCircle, Warning, Copy, Plugs, Heartbeat, WebhooksLogo, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PROVIDERS = [
  {
    name: "Render",
    tag: "Recomendado",
    color: "emerald",
    free: "Plano Free 0,5 GB RAM, dorme após 15 min sem uso (acorda em ~30s)",
    url: "https://render.com",
    steps: [
      "Crie conta gratuita em render.com com sua conta GitHub",
      "Push o código para um repo GitHub (público ou privado)",
      "No Render: New + → Blueprint → escolha o repo (lê render.yaml automaticamente)",
      "Configure variáveis de ambiente: MONGO_URL, DB_NAME, JWT_SECRET, CORS_ORIGINS, DEFAULT_WHATSAPP",
      "Render gera URL https://seu-app.onrender.com — cole esse valor em REACT_APP_BACKEND_URL no frontend e refaça o build",
    ],
  },
  {
    name: "Railway",
    tag: "Mais rápido",
    color: "blue",
    free: "$5 de crédito mensal grátis, sem dormir, MongoDB pode ser provisionado",
    url: "https://railway.app",
    steps: [
      "Crie conta em railway.app via GitHub",
      "New Project → Deploy from GitHub Repo → escolha seu repo",
      "Configure Root Directory para 'backend'",
      "Settings → Environment → cole: MONGO_URL, DB_NAME, JWT_SECRET, CORS_ORIGINS, DEFAULT_WHATSAPP",
      "Adicione um plugin MongoDB no projeto (ou use Atlas externo)",
      "Settings → Networking → Generate Domain — copia URL pública",
    ],
  },
  {
    name: "Fly.io",
    tag: "Sempre on",
    color: "purple",
    free: "256MB RAM grátis, sem dormir, ótimo para apps globais",
    url: "https://fly.io",
    steps: [
      "Instale o flyctl: curl -L https://fly.io/install.sh | sh",
      "fly auth login (vai abrir o navegador)",
      "No diretório /backend: fly launch (ele cria fly.toml e Dockerfile)",
      "Configure secrets: fly secrets set MONGO_URL=... JWT_SECRET=... CORS_ORIGINS=...",
      "fly deploy — pronto, app online em https://seu-app.fly.dev",
    ],
  },
];

// All public API endpoints
const API_GROUPS = [
  {
    title: "Setup & Autenticação",
    icon: Lock,
    color: "blue",
    items: [
      { method: "GET",  path: "/api/setup/status",     desc: "Verifica se precisa criar primeiro admin", auth: false },
      { method: "POST", path: "/api/setup/admin",      desc: "Cria primeiro admin (apenas 1x)", auth: false },
      { method: "POST", path: "/api/auth/login",       desc: "Login admin ou cliente, retorna JWT", auth: false },
      { method: "GET",  path: "/api/auth/me",          desc: "Dados do usuário logado", auth: "Bearer" },
    ],
  },
  {
    title: "Admin — Clientes",
    icon: Plugs,
    color: "purple",
    items: [
      { method: "GET",    path: "/api/admin/clients",            desc: "Listar clientes (filter: em_dia/proximo/vencido/inativo)", auth: "Admin" },
      { method: "POST",   path: "/api/admin/clients",            desc: "Cadastrar novo cliente", auth: "Admin" },
      { method: "PUT",    path: "/api/admin/clients/{id}",       desc: "Atualizar cliente", auth: "Admin" },
      { method: "DELETE", path: "/api/admin/clients/{id}",       desc: "Excluir cliente + histórico", auth: "Admin" },
    ],
  },
  {
    title: "Admin — Planos & Pagamentos",
    icon: Plugs,
    color: "emerald",
    items: [
      { method: "GET",    path: "/api/admin/plans",              desc: "Listar planos", auth: "Admin" },
      { method: "POST",   path: "/api/admin/plans",              desc: "Criar plano (nome, valor, recorrência)", auth: "Admin" },
      { method: "PUT",    path: "/api/admin/plans/{id}",         desc: "Atualizar plano", auth: "Admin" },
      { method: "DELETE", path: "/api/admin/plans/{id}",         desc: "Excluir plano", auth: "Admin" },
      { method: "GET",    path: "/api/admin/payments",           desc: "Histórico de pagamentos", auth: "Admin" },
      { method: "POST",   path: "/api/admin/payments",           desc: "Registrar pagamento manual + avança vencimento", auth: "Admin" },
      { method: "DELETE", path: "/api/admin/payments/{id}",      desc: "Excluir pagamento", auth: "Admin" },
    ],
  },
  {
    title: "Admin — Dashboard, Notificações & Settings",
    icon: Plugs,
    color: "amber",
    items: [
      { method: "GET",  path: "/api/admin/dashboard",        desc: "Métricas: total, em dia, próximos, vencidos, MRR, distribuição por nível", auth: "Admin" },
      { method: "GET",  path: "/api/admin/notifications",    desc: "Histórico de notificações enviadas", auth: "Admin" },
      { method: "POST", path: "/api/admin/notifications/send", desc: "Enviar notificação manual para 1+ clientes", auth: "Admin" },
      { method: "GET",  path: "/api/admin/settings",         desc: "Ler configurações (WhatsApp, fidelidade, MP)", auth: "Admin" },
      { method: "PUT",  path: "/api/admin/settings",         desc: "Atualizar configurações", auth: "Admin" },
      { method: "POST", path: "/api/admin/settings/mp-test", desc: "Testa Access Token Mercado Pago", auth: "Admin" },
    ],
  },
  {
    title: "Admin — Billing (Mercado Pago)",
    icon: Plugs,
    color: "cyan",
    items: [
      { method: "POST",   path: "/api/admin/billing/preapproval/{user_id}",   desc: "Criar autopay (recorrente) para cliente", auth: "Admin" },
      { method: "DELETE", path: "/api/admin/billing/preapproval/{user_id}",   desc: "Cancelar autopay", auth: "Admin" },
      { method: "POST",   path: "/api/admin/billing/payment-link/{user_id}",  desc: "Gerar link de pagamento avulso (Pix/cartão/boleto)", auth: "Admin" },
    ],
  },
  {
    title: "Cliente",
    icon: Plugs,
    color: "rose",
    items: [
      { method: "GET",  path: "/api/client/me",                   desc: "Próprios dados + plano + fidelidade", auth: "Bearer (client)" },
      { method: "GET",  path: "/api/client/payments",             desc: "Meu histórico de pagamentos", auth: "Bearer (client)" },
      { method: "GET",  path: "/api/client/notifications",        desc: "Minhas notificações", auth: "Bearer (client)" },
      { method: "POST", path: "/api/client/notifications/{id}/read", desc: "Marcar notificação como lida", auth: "Bearer (client)" },
      { method: "GET",  path: "/api/client/billing/status",       desc: "Status autopay + valor + vencimento", auth: "Bearer (client)" },
      { method: "POST", path: "/api/client/billing/preapproval",  desc: "Ativar pagamento automático (1-clique)", auth: "Bearer (client)" },
      { method: "DELETE", path: "/api/client/billing/preapproval", desc: "Cancelar pagamento automático", auth: "Bearer (client)" },
      { method: "POST", path: "/api/client/billing/payment-link", desc: "Gerar link de pagamento avulso", auth: "Bearer (client)" },
    ],
  },
  {
    title: "Públicos & Cron",
    icon: Heartbeat,
    color: "zinc",
    items: [
      { method: "GET",  path: "/api/",                desc: "Health check completo (com app info)", auth: false },
      { method: "GET",  path: "/api/health",          desc: "Health leve para monitores (sem DB)", auth: false },
      { method: "GET",  path: "/api/public/settings", desc: "Configurações públicas (company_name, whatsapp)", auth: false },
      { method: "GET",  path: "/api/public/whatsapp", desc: "Link wa.me com mensagem pronta", auth: false },
      { method: "POST", path: "/api/cron/run",        desc: "Gerar lembretes automáticos -7/-3/-2/0/+1/+3/+5 dias", auth: false },
    ],
  },
];

const METHOD_COLOR = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-blue-50 text-blue-700 border-blue-200",
  PUT: "bg-amber-50 text-amber-700 border-amber-200",
  DELETE: "bg-rose-50 text-rose-700 border-rose-200",
};

const Step = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white grid place-items-center text-[11px] font-semibold">{n}</span>
    <div className="text-sm text-zinc-700 leading-relaxed">{children}</div>
  </li>
);

const Code2 = ({ children }) => {
  const copy = () => { navigator.clipboard.writeText(String(children)); toast.success("Copiado"); };
  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre">
        <code>{children}</code>
      </pre>
      <button onClick={copy} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded p-1.5">
        <Copy size={12}/>
      </button>
    </div>
  );
};

const BackendUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL || "";
  const copy = () => { navigator.clipboard.writeText(url); toast.success("URL do backend copiada"); };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-2 text-xs font-mono bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 rounded px-2 py-1 transition"
      title="Copiar"
    >
      {url}<Copy size={10}/>
    </button>
  );
};

const Deploy = () => {
  const [activeTab, setActiveTab] = useState("hospedagem");
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://SEU-BACKEND.onrender.com";

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Deploy & Integrações</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Backend & APIs</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl">
          Tudo o que você precisa para hospedar, manter ativo, integrar APIs e configurar webhooks.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-zinc-500">Backend atual:</span>
          <BackendUrl />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 gap-1 w-full sm:w-auto">
          <TabsTrigger value="hospedagem" data-testid="tab-hospedagem"><Cloud size={14} className="mr-1.5"/>Hospedagem</TabsTrigger>
          <TabsTrigger value="keepalive" data-testid="tab-keepalive"><Heartbeat size={14} className="mr-1.5"/>Keep-Alive</TabsTrigger>
          <TabsTrigger value="apis" data-testid="tab-apis"><Plugs size={14} className="mr-1.5"/>APIs</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><WebhooksLogo size={14} className="mr-1.5"/>Webhooks</TabsTrigger>
        </TabsList>

        {/* HOSPEDAGEM */}
        <TabsContent value="hospedagem" className="mt-6 space-y-6">
          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center"><Cloud size={22} weight="duotone"/></div>
              <div className="flex-1">
                <div className="font-display text-lg font-semibold">Como funciona</div>
                <p className="text-sm text-zinc-600 mt-1">
                  Seu sistema tem dois componentes: <strong>frontend</strong> (PWA estático na Hostinger) e <strong>backend</strong> (FastAPI + MongoDB). O backend pode rodar em qualquer hospedagem Python — escolha uma abaixo.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-amber-200 bg-amber-50/40 p-6 card-soft">
            <div className="flex items-start gap-3">
              <Warning size={22} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-display text-base font-semibold">Antes de começar</div>
                <ul className="mt-3 space-y-2 text-sm text-zinc-700">
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/> Crie conta no <a href="https://github.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">GitHub</a> e faça push do código</li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/> Crie cluster grátis no <a href="https://www.mongodb.com/cloud/atlas" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">MongoDB Atlas</a> (M0 — 512MB para sempre)</li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/> Libere IP <code className="bg-zinc-100 px-1 rounded text-xs">0.0.0.0/0</code> em Network Access do Atlas</li>
                </ul>
              </div>
            </div>
          </Card>

          {PROVIDERS.map((p) => (
            <Card key={p.name} className="border-zinc-200 p-6 card-soft" data-testid={`provider-${p.name.toLowerCase()}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-${p.color}-50 text-${p.color}-700 grid place-items-center`}>
                    <Rocket size={22} weight="duotone"/>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display text-xl font-semibold">{p.name}</h2>
                      <span className={`text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-full bg-${p.color}-100 text-${p.color}-700 border border-${p.color}-200`}>{p.tag}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{p.free}</div>
                  </div>
                </div>
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <a href={p.url} target="_blank" rel="noreferrer">Acessar <ArrowSquareOut size={14}/></a>
                </Button>
              </div>
              <ol className="mt-5 space-y-3">
                {p.steps.map((s, i) => <Step key={i} n={i + 1}>{s}</Step>)}
              </ol>
            </Card>
          ))}

          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center"><Lightning size={18} weight="duotone"/></div>
              <div className="font-display text-lg font-semibold">Variáveis de ambiente do backend</div>
            </div>
            <Code2>{`MONGO_URL=mongodb+srv://USER:SENHA@cluster.mongodb.net/?retryWrites=true&w=majority
DB_NAME=fidelis
JWT_SECRET=GERE_UM_HASH_LONGO_SEGURO_AQUI
JWT_EXPIRE_HOURS=168
CORS_ORIGINS=https://seudominio.com.br,https://www.seudominio.com.br
DEFAULT_WHATSAPP=5532999103556`}</Code2>
            <p className="text-xs text-zinc-500 mt-3">
              Gere um <code className="bg-zinc-100 px-1 rounded">JWT_SECRET</code> seguro: <code className="bg-zinc-100 px-1 rounded">openssl rand -hex 32</code>
            </p>
          </Card>
        </TabsContent>

        {/* KEEP-ALIVE */}
        <TabsContent value="keepalive" className="mt-6 space-y-6">
          <Card className="border-emerald-200 bg-emerald-50/30 p-6 card-soft">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Heartbeat size={22} weight="duotone"/></div>
              <div className="flex-1">
                <div className="font-display text-lg font-semibold">Mantenha seu Render sempre acordado</div>
                <p className="text-sm text-zinc-700 mt-1">
                  No plano <strong>Free</strong> do Render, o app <strong>dorme após 15 min sem requisições</strong> e demora ~30s para acordar. Use um monitor externo gratuito para fazer ping a cada 5-10 minutos.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Recomendado</Badge>
              <h3 className="font-display text-lg font-semibold">UptimeRobot (grátis)</h3>
            </div>
            <ol className="space-y-3 mb-4">
              <Step n="1">Crie conta em <a href="https://uptimerobot.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">uptimerobot.com <ArrowSquareOut size={12}/></a></Step>
              <Step n="2">Dashboard → <strong>+ Add New Monitor</strong></Step>
              <Step n="3">Tipo: <code className="bg-zinc-100 px-1 rounded text-xs">HTTP(s)</code> · Intervalo: <code className="bg-zinc-100 px-1 rounded text-xs">5 minutes</code> (grátis até 50 monitores)</Step>
              <Step n="4">URL a monitorar:</Step>
            </ol>
            <Code2>{`${backendUrl}/api/health`}</Code2>
            <ol className="space-y-3 mt-4" start="5">
              <Step n="5">Salve. Pronto — seu backend nunca mais vai dormir! 🎉</Step>
            </ol>
            <div className="mt-4 text-xs text-zinc-500 border-t border-zinc-100 pt-3">
              💡 O endpoint <code className="bg-zinc-100 px-1 rounded">/api/health</code> é otimizado (sem acessar banco) para minimizar carga.
            </div>
          </Card>

          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">Alternativa A</Badge>
              <h3 className="font-display text-lg font-semibold">Cron-job.org (grátis, ilimitado)</h3>
            </div>
            <ol className="space-y-3 mb-3">
              <Step n="1">Crie conta em <a href="https://cron-job.org" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">cron-job.org <ArrowSquareOut size={12}/></a></Step>
              <Step n="2">CronJobs → <strong>Create cronjob</strong></Step>
              <Step n="3">URL: <code className="bg-zinc-100 px-1 rounded text-xs">{backendUrl}/api/health</code> · Schedule: <strong>Every 10 minutes</strong></Step>
              <Step n="4">Save. Funciona 24/7 sem limite de jobs.</Step>
            </ol>
            <div className="text-xs text-zinc-500 border-t border-zinc-100 pt-3">
              💡 Bônus: você também pode usar o cron-job.org para chamar <code className="bg-zinc-100 px-1 rounded">/api/cron/run</code> 1x/dia para gerar lembretes automáticos.
            </div>
          </Card>

          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">Alternativa B</Badge>
              <GithubLogo size={18} className="text-zinc-700"/>
              <h3 className="font-display text-lg font-semibold">GitHub Actions (grátis, sem cadastro extra)</h3>
            </div>
            <p className="text-sm text-zinc-600 mb-3">Crie o arquivo <code className="bg-zinc-100 px-1 rounded text-xs">.github/workflows/keep-alive.yml</code> no seu repo:</p>
            <Code2>{`name: Keep Render Alive
on:
  schedule:
    - cron: "*/10 * * * *"   # a cada 10 minutos
  workflow_dispatch:

jobs:
  ping:
    runs-on: ubuntu-latest
    steps:
      - name: Ping health endpoint
        run: curl -fsS ${backendUrl}/api/health || true
      - name: Trigger lembretes automáticos (1x/dia, 9h UTC = 6h BR)
        if: github.event.schedule == '0 9 * * *' || github.event_name == 'workflow_dispatch'
        run: curl -fsS -X POST ${backendUrl}/api/cron/run || true`}</Code2>
            <p className="text-xs text-zinc-500 mt-3">
              ⚠️ GitHub Actions pode atrasar o cron em alguns minutos quando há alta carga global. Para uptime mais previsível, prefira UptimeRobot.
            </p>
          </Card>

          <Card className="border-blue-200 bg-blue-50/30 p-6 card-soft">
            <div className="flex items-start gap-3">
              <CheckCircle weight="fill" className="text-blue-600 mt-0.5" size={20}/>
              <div className="flex-1">
                <div className="font-display text-base font-semibold">Recomendação combinada</div>
                <p className="text-sm text-zinc-700 mt-1">
                  Use <strong>UptimeRobot a cada 5 min</strong> para keep-alive + <strong>cron-job.org 1x/dia às 9h</strong> chamando <code className="bg-white px-1 rounded text-xs">POST /api/cron/run</code> para enviar lembretes automáticos. Tudo gratuito.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* APIs */}
        <TabsContent value="apis" className="mt-6 space-y-6">
          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-zinc-900 text-white grid place-items-center"><Plugs size={18} weight="duotone"/></div>
              <h3 className="font-display text-lg font-semibold">Catálogo completo de endpoints</h3>
            </div>
            <p className="text-sm text-zinc-600 mb-4">Todos os endpoints prefixados com <code className="bg-zinc-100 px-1 rounded text-xs">/api</code>. Base URL: <code className="bg-zinc-100 px-1 rounded text-xs">{backendUrl}</code></p>

            <div className="flex flex-wrap gap-2 text-[10px] tracking-widest uppercase font-semibold mb-4">
              <span className="px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">GET</span>
              <span className="px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">POST</span>
              <span className="px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">PUT</span>
              <span className="px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">DELETE</span>
            </div>
          </Card>

          {API_GROUPS.map((grp) => (
            <Card key={grp.title} className="border-zinc-200 p-0 card-soft overflow-hidden" data-testid={`api-group-${grp.title}`}>
              <div className={`px-5 py-3 border-b border-zinc-200 bg-${grp.color}-50/40 flex items-center gap-2`}>
                <grp.icon size={18} weight="duotone" className={`text-${grp.color}-700`}/>
                <h3 className="font-display font-semibold">{grp.title}</h3>
                <span className="ml-auto text-xs text-zinc-500">{grp.items.length} endpoints</span>
              </div>
              <ul className="divide-y divide-zinc-100">
                {grp.items.map((it) => (
                  <li key={`${it.method}-${it.path}`} className="px-5 py-3 hover:bg-zinc-50/60 flex items-start gap-3 flex-wrap">
                    <span className={`text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded border font-mono ${METHOD_COLOR[it.method] || ""}`}>
                      {it.method}
                    </span>
                    <code className="text-xs font-mono text-zinc-900 break-all">{it.path}</code>
                    <span className="text-sm text-zinc-600 w-full sm:flex-1 sm:w-auto sm:text-right">{it.desc}</span>
                    {it.auth && (
                      <span className="text-[10px] tracking-widest uppercase font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200 flex items-center gap-1">
                        <Lock size={10} weight="bold"/> {it.auth}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ))}

          <Card className="border-zinc-200 p-6 card-soft">
            <h3 className="font-display text-base font-semibold mb-3">Exemplo de uso (curl)</h3>
            <Code2>{`# 1) Login admin
curl -X POST ${backendUrl}/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"username":"seu_usuario","password":"sua_senha"}'

# Resposta: {"access_token":"eyJ...", "user": {...}}

# 2) Usar o token
curl ${backendUrl}/api/admin/dashboard \\
  -H "Authorization: Bearer eyJ..."

# 3) Gerar lembretes automáticos
curl -X POST ${backendUrl}/api/cron/run`}</Code2>
          </Card>
        </TabsContent>

        {/* WEBHOOKS */}
        <TabsContent value="webhooks" className="mt-6 space-y-6">
          <Card className="border-zinc-200 p-6 card-soft">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-700 grid place-items-center"><WebhooksLogo size={22} weight="duotone"/></div>
              <div className="flex-1">
                <div className="font-display text-lg font-semibold">Webhook do Mercado Pago</div>
                <p className="text-sm text-zinc-600 mt-1">
                  Configure o Mercado Pago para enviar notificações automaticamente quando um pagamento é aprovado ou cancelado. Assim seu sistema atualiza o status do cliente em tempo real, sem você precisar atualizar nada manualmente.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-zinc-200 p-6 card-soft">
            <h3 className="font-display text-lg font-semibold mb-4">Configurar no painel Mercado Pago</h3>
            <ol className="space-y-3 mb-4">
              <Step n="1">Acesse <a href="https://www.mercadopago.com.br/developers/panel/webhooks" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">painel de webhooks <ArrowSquareOut size={12}/></a></Step>
              <Step n="2">Selecione sua aplicação (Zeferius Pay)</Step>
              <Step n="3">Clique em <strong>Configurar notificações</strong> → <strong>Webhooks</strong></Step>
              <Step n="4">URL de produção (cole exatamente):</Step>
            </ol>
            <Code2>{`${backendUrl}/api/webhooks/mercadopago`}</Code2>
            <ol className="space-y-3 mt-4" start="5">
              <Step n="5">Em <strong>Eventos</strong>, marque:
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">✓ Pagamento</span>
                  <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">✓ Assinatura (preapproval)</span>
                  <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">✓ Plano (preapproval plan)</span>
                </div>
              </Step>
              <Step n="6">Clique em <strong>Salvar</strong>. Pronto!</Step>
              <Step n="7">(Opcional) Copie a <strong>Chave Secreta</strong> que o MP gerar e cole no painel Zeferius Pay em Configurações → Mercado Pago → Webhook Secret</Step>
            </ol>
          </Card>

          <Card className="border-zinc-200 p-6 card-soft">
            <h3 className="font-display text-base font-semibold mb-3">O que o webhook faz no Zeferius Pay</h3>
            <ul className="space-y-2 text-sm text-zinc-700">
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                <span><strong>Pagamento aprovado</strong> → registra automaticamente em Pagamentos, avança o próximo vencimento e atualiza o status do cliente para "Em dia"</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                <span><strong>Preapproval authorized</strong> → ativa o badge "Auto" no cliente e marca <code className="bg-zinc-100 px-1 rounded text-xs">auto_renew=true</code></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                <span><strong>Preapproval cancelled</strong> → desativa o autopay e notifica o admin</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                <span><strong>Idempotente</strong>: se o MP enviar a mesma notificação 2x (acontece), não duplica o pagamento</span>
              </li>
            </ul>
          </Card>

          <Card className="border-amber-200 bg-amber-50/30 p-6 card-soft">
            <div className="flex items-start gap-3">
              <Warning weight="fill" size={20} className="text-amber-600 mt-0.5"/>
              <div>
                <div className="font-display text-base font-semibold">Testando o webhook</div>
                <p className="text-sm text-zinc-700 mt-1">
                  No painel MP de webhooks, há um botão <strong>"Simular notificação"</strong>. Use para testar sem precisar fazer um pagamento real. Os logs do backend mostrarão a chamada recebida.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Deploy;
